// Local, per-employment draft storage for in-progress form data, so a session timeout / reload
// never loses an unsaved note. The clinical content (`data`) is AES-GCM encrypted with the
// employment's key (see draftCrypto); the envelope keeps `savedAt` / `employmentId` /
// `dossierId` in clear so a dossier can be matched without decrypting every entry. Keyed by the
// react-redux-form model path of the entity (e.g. `entities.dossierItem.byId.<id>`).
//
// The draft functions take the whole `employment` so the call sites stay simple — id (the
// localStorage namespace) and secret (the encryption key) are read off it here.

import { decrypt, encrypt } from 'draftCrypto'
import isUuid from 'is_uuid'

const PREFIX = 'zorgmee:draft:'

export function draftKey(modelPath, employmentId) {
  return `${PREFIX}${employmentId}:${modelPath}`
}

export async function saveDraft(modelPath, employment, data) {
  const id = employment?.id
  const secret = employment?.draftEncryptionKey
  if (!modelPath || !id || !secret) return
  try {
    const encryptedData = await encrypt(JSON.stringify(data), secret)
    localStorage.setItem(
      draftKey(modelPath, id),
      JSON.stringify({ savedAt: Date.now(), employmentId: id, dossierId: data && data.dossierId, encryptedData })
    )
  } catch (e) {
    // storage full / disabled / crypto unavailable — drafts are best-effort, never block the app.
  }
}

// Returns `{ savedAt, employmentId, dossierId, data }` (decrypting `data`), or null when
// missing / belongs to another employment / unreadable. The draft is removed when the note
// modal closes (see clearDraft), not on read — so it survives a restore that isn't acted on.
export async function loadDraft(modelPath, employment) {
  const id = employment?.id
  const secret = employment?.draftEncryptionKey
  if (!modelPath || !id || !secret) return null
  try {
    const key = draftKey(modelPath, id)
    const raw = localStorage.getItem(key)

    if (!raw) return null
    const env = JSON.parse(raw)
    if (!env || env.employmentId !== id || !env.encryptedData) return null

    const data = JSON.parse(await decrypt(env.encryptedData, secret))
    localStorage.removeItem(key)
    return { ...env, data }
  } catch (e) {
    return null
  }
}

// Removes a draft (no decryption needed — only the key namespace matters). Used when a note
// modal closes (saved or just closed) so a finished/abandoned draft doesn't linger or re-appear.
export function clearDraft(modelPath, employment) {
  const id = employment?.id
  if (!modelPath || !id) return
  try {
    localStorage.removeItem(draftKey(modelPath, id))
  } catch (e) {
    // ignore
  }
}

// Finds this employment's most-recently-saved draft for a given dossier (and removes it).
// Restore happens at the dossier level (DossierFollowUp), where the original dossierItem id
// isn't known anymore (a new item's UUID / a loaded item both get flushed from the store), so
// we match on the plaintext `employmentId` / `dossierId` in the envelope and only decrypt the
// chosen one. Returns `{ savedAt, employmentId, dossierId, data }` (whose `data.id` is a UUID
// for a new note, numeric for an edit), or null.
export async function findDraftForDossier(dossierId, employment) {
  const id = employment?.id
  const secret = employment?.draftEncryptionKey
  if (!dossierId || !id || !secret) return null
  let best = null
  let bestKey = null
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (!key || key.indexOf(PREFIX) !== 0) continue
      if (!isUuid(key.slice(key.lastIndexOf('.') + 1))) continue // new notes only; skip edits

      let env = null
      try {
        env = JSON.parse(localStorage.getItem(key))
      } catch (e) {
        env = null
      }
      if (!env || env.employmentId !== id || !env.encryptedData) continue
      if (String(env.dossierId) !== String(dossierId)) continue

      if (!best || (env.savedAt || 0) > (best.savedAt || 0)) {
        best = env
        bestKey = key
      }
    }

    if (!best) return null
    const data = JSON.parse(await decrypt(best.encryptedData, secret))
    localStorage.removeItem(bestKey)
    return { ...best, data }
  } catch (e) {
    return null
  }
}
